import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import styles from './RapportModal.module.css';
import LoadingSpinner from './LoadingSpinner';
import ProjectSelectionModal from './ProjectSelectionModal';

const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';

const RapportModal = ({ isOpen, onClose, todayData, selectedDate, buckets }) => {
  const [rapportData, setRapportData] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [zohoEmail, setZohoEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(true);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editingTicketIndex, setEditingTicketIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({}); // { ticketId: 'success' | 'error' | 'skipped' }
  const [notification, setNotification] = useState(null); // { type: 'success' | 'error' | 'warning', message: string }
  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Modal just opened - reset and generate once
      setSubmitStatus({});
      setNotification(null);
      setIsSubmitting(false);
      loadZohoEmail();
      if (todayData && todayData.length > 0) {
        generateRapports();
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  const loadZohoEmail = async () => {
    setEmailLoading(true);
    try {
      const response = await axios.get(`${API_URL}/users/preferences`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setZohoEmail(response.data.zohoEmail || null);
    } catch (error) {
      console.error('Error loading Zoho email:', error);
      setZohoEmail(null);
    } finally {
      setEmailLoading(false);
    }
  };

  const generateRapports = async () => {
    setIsGenerating(true);
    // Fix 06.10.2026: Nur Tickets mit echten Zeitbuchungen an dem Tag rapportieren -
    // Step-/Sync-Phantome (kein TimeEntry am Tag) gehoeren nicht in den Rapport
    const groupsWithTime = todayData.filter(group => group.entries.reduce((acc, entry) => acc + entry.duration, 0) > 0);
    try {
      // Helper: Steps splitten in heute (Rapport-Inhalt) und früher (Kontext)
      const splitStepsByDate = (stepTimestamps, targetDate) => {
        if (!stepTimestamps || !Array.isArray(stepTimestamps)) return { today: [], earlier: [] };

        // Parse target date to get start and end of day
        // Fix 06.10.2026: Endgrenze ergaenzt - ohne sie landeten bei einem Rapport
        // fuer ein frueheres Datum ALLE spaeteren Steps als "heute" im Rapport-Inhalt
        const targetDateObj = new Date(targetDate);
        const startOfDay = new Date(targetDateObj.setHours(0, 0, 0, 0)).getTime();
        const endOfDay = new Date(targetDateObj.setHours(23, 59, 59, 999)).getTime();

        const today = [];
        const earlier = [];
        stepTimestamps.forEach(step => {
          if (!step.createdAt) return;
          const stepTime = new Date(step.createdAt).getTime();
          if (stepTime >= startOfDay && stepTime <= endOfDay) {
            today.push(step.text);
          } else {
            earlier.push(`${new Date(step.createdAt).toLocaleDateString('de-DE')}: ${step.text}`);
          }
        });
        return { today, earlier };
      };

      // Prepare tickets for AI processing - Steps von heute (Rapport-Inhalt) + frühere Steps + Todos (Kontext)
      const ticketsForAI = groupsWithTime.map(group => {
        const { today, earlier } = splitStepsByDate(group.ticket.stepTimestamps, selectedDate);
        return {
          ticketId: group.ticket._id,
          title: group.ticket.title,
          description: group.ticket.description,
          project: group.ticket.projectRef?.name || group.ticket.project,
          duration: group.entries.reduce((acc, entry) => acc + entry.duration, 0),
          steps: today,
          stepsEarlier: earlier,
          todos: group.ticket.todos || []
        };
      });

      console.log('Generating rapports for tickets:', ticketsForAI);

      // Call AI API to generate rapport suggestions
      const response = await axios.post(`${API_URL}/ai/generate-rapport`, {
        tickets: ticketsForAI,
        date: selectedDate
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('AI rapport response:', response.data);

      // Combine original ticket data with AI suggestions
      const rapportItems = groupsWithTime.map(group => {
        const aiSuggestion = response.data.rapports?.find(r => r.ticketId === group.ticket._id);
        const suggestionText = aiSuggestion?.suggestion || '';
        console.log(`Ticket ${group.ticket._id}: AI suggestion = "${suggestionText}"`);
        return {
          ticket: group.ticket,
          duration: group.entries.reduce((acc, entry) => acc + entry.duration, 0),
          aiSuggestion: suggestionText,
          userInput: suggestionText,
          included: true // Default to included
        };
      });

      console.log('Final rapport items:', rapportItems);
      setRapportData(rapportItems);
    } catch (error) {
      console.error('Error generating rapports:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Fallback: create rapport items without AI suggestions
      const fallbackItems = groupsWithTime.map(group => ({
        ticket: group.ticket,
        duration: group.entries.reduce((acc, entry) => acc + entry.duration, 0),
        aiSuggestion: '',
        userInput: '',
        included: true
      }));
      setRapportData(fallbackItems);
      setNotification({ type: 'warning', message: 'AI-Vorschläge konnten nicht generiert werden. Bitte manuell eingeben.' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleInputChange = (index, value) => {
    const newRapportData = [...rapportData];
    newRapportData[index].userInput = value;
    setRapportData(newRapportData);
  };

  const handleToggleIncluded = (index) => {
    const newRapportData = [...rapportData];
    newRapportData[index].included = !newRapportData[index].included;
    setRapportData(newRapportData);
  };

  const handleOpenProjectModal = (index) => {
    setEditingTicketIndex(index);
    setProjectModalOpen(true);
  };

  const handleProjectSelect = async ({ project, task }) => {
    if (editingTicketIndex === null) return;

    const ticketId = rapportData[editingTicketIndex].ticket._id;

    try {
      // Update ticket in backend
      await axios.put(`${API_URL}/tickets/${ticketId}`, {
        projectRef: project?._id || null,
        taskRef: task?._id || null
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Update local state
      const newRapportData = [...rapportData];
      newRapportData[editingTicketIndex].ticket = {
        ...newRapportData[editingTicketIndex].ticket,
        projectRef: project,
        taskRef: task
      };
      setRapportData(newRapportData);
    } catch (error) {
      console.error('Error updating ticket project:', error);
      setNotification({ type: 'error', message: 'Fehler beim Aktualisieren des Projekts' });
    }

    setProjectModalOpen(false);
    setEditingTicketIndex(null);
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setRapportData([]);
    }, 300);
  };

  const handleSubmit = async () => {
    // Double-submit protection
    if (isSubmitting) return;
    setIsSubmitting(true);
    setNotification(null);

    try {
      // Track status per ticket
      const newStatus = {};

      // Filter only included tickets that have project and task assigned
      const validEntries = rapportData
        .filter(item => item.included && item.duration > 0 && item.ticket.projectRef && item.ticket.taskRef)
        .map(item => {
          // Convert duration from seconds to HH:MM format
          const totalMinutes = Math.round(item.duration / 60);
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          const logTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

          return {
            projectId: item.ticket.projectRef.zohoProjectId,
            taskId: item.ticket.taskRef.zohoTaskId,
            logDate: selectedDate,
            logTime: logTime,
            notes: item.userInput || item.ticket.title,
            // Include ticket info for tracking
            _ticketId: item.ticket._id,
            _ticketTitle: item.ticket.title,
            _projectName: item.ticket.projectRef.name
          };
        });

      // Mark skipped entries (included but no project/task)
      rapportData.forEach(item => {
        if (item.included && (!item.ticket.projectRef || !item.ticket.taskRef)) {
          newStatus[item.ticket._id] = 'skipped';
        }
      });

      const includedCount = rapportData.filter(item => item.included).length;
      const excludedCount = rapportData.length - includedCount;

      if (validEntries.length === 0) {
        if (includedCount === 0) {
          setNotification({ type: 'warning', message: 'Keine Einträge ausgewählt. Bitte wähle mindestens einen Eintrag aus.' });
        } else {
          setNotification({ type: 'warning', message: 'Keine Tickets mit Zoho-Projekt und Aufgabe gefunden. Bitte weise zuerst ein Projekt zu.' });
        }
        setSubmitStatus(newStatus);
        setIsSubmitting(false);
        return;
      }

      console.log('Submitting time entries:', validEntries, 'for user:', zohoEmail);

      const response = await axios.post(`${API_URL}/zoho/time-entries`, {
        entries: validEntries,
        userEmail: zohoEmail
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Zoho response:', response.data);

      // Mark successful entries
      if (response.data.created) {
        response.data.created.forEach(entry => {
          if (entry._ticketId) {
            newStatus[entry._ticketId] = 'success';
          }
        });
      }

      // Mark failed entries
      if (response.data.errors) {
        response.data.errors.forEach(err => {
          if (err.entry?._ticketId) {
            newStatus[err.entry._ticketId] = 'error';
          }
        });
      }

      setSubmitStatus(newStatus);

      // Deselect successfully submitted tickets so they can't be double-submitted
      const successIds = new Set(
        Object.entries(newStatus).filter(([, s]) => s === 'success').map(([id]) => id)
      );
      if (successIds.size > 0) {
        setRapportData(prev => prev.map(item =>
          successIds.has(item.ticket._id) ? { ...item, included: false } : item
        ));
      }

      const successCount = response.data.created?.length || 0;
      const errorCount = response.data.errors?.length || 0;
      const skippedCount = Object.values(newStatus).filter(s => s === 'skipped').length;

      // Build notification message
      let message = '';
      let notificationType = 'success';

      if (successCount > 0) {
        message += `${successCount} Zeiteintrag${successCount !== 1 ? 'e' : ''} erfolgreich gesendet`;
      }

      if (errorCount > 0) {
        notificationType = 'warning';
        if (message) message += ' • ';
        message += `${errorCount} fehlgeschlagen`;
      }

      if (skippedCount > 0) {
        if (message) message += ' • ';
        message += `${skippedCount} übersprungen (kein Projekt)`;
      }

      if (excludedCount > 0) {
        if (message) message += ' • ';
        message += `${excludedCount} ausgeschlossen`;
      }

      // If there are errors, keep modal open
      if (errorCount > 0 || skippedCount > 0) {
        setNotification({ type: notificationType, message });
        setIsSubmitting(false);
      } else {
        // Full success - show notification briefly and close
        setNotification({ type: 'success', message });
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error submitting to Zoho:', error);
      setNotification({ type: 'error', message: 'Fehler beim Senden: ' + (error.response?.data?.message || error.message) });
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (isGenerating) {
    return <LoadingSpinner />;
  }

  return (
    <div className={`${styles.overlay} ${isClosing ? styles.closing : ''}`}>
      <div className={`${styles.modal} ${isClosing ? styles.closing : ''}`}>
        <div className={styles.header}>
          <h2>AI Rapport</h2>
          <button onClick={handleClose} className={styles.closeButton}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className={styles.dateInfo}>
          <span>Datum: {new Date(selectedDate).toLocaleDateString()}</span>
        </div>

        {notification && (
          <div className={`${styles.notification} ${styles[notification.type]}`}>
            <span className={styles.notificationIcon}>
              {notification.type === 'success' && '✓'}
              {notification.type === 'warning' && '⚠'}
              {notification.type === 'error' && '✕'}
            </span>
            <span className={styles.notificationMessage}>{notification.message}</span>
            <button
              className={styles.notificationClose}
              onClick={() => setNotification(null)}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}

        <div className={styles.content}>
          {!emailLoading && !zohoEmail && (
            <div className={styles.emailWarning}>
              <i className="bi bi-exclamation-triangle-fill"></i>
              <div>
                <strong>Keine Zoho E-Mail hinterlegt</strong>
                <p>Bitte hinterlege deine Zoho E-Mail-Adresse in den Einstellungen, um Zeiteinträge an Zoho senden zu können.</p>
              </div>
            </div>
          )}
          {rapportData.length === 0 ? (
            <p className={styles.noData}>Keine Daten zum Generieren eines Rapports.</p>
          ) : (
            <div className={styles.rapportList}>
              {rapportData.map((item, index) => {
                const bucket = buckets[item.ticket.bucket];
                const hasProject = item.ticket.projectRef?.name || item.ticket.project;
                const hasTask = item.ticket.taskRef?.name;
                const status = submitStatus[item.ticket._id];
                return (
                  <div key={index} className={`${styles.rapportItem} ${!item.included ? styles.excluded : ''} ${status === 'success' ? styles.submitted : ''} ${status === 'error' ? styles.failed : ''} ${status === 'skipped' ? styles.skipped : ''}`}>
                    <div className={styles.checkboxContainer}>
                      <input
                        type="checkbox"
                        checked={item.included}
                        onChange={() => handleToggleIncluded(index)}
                        className={styles.checkbox}
                        title={item.included ? 'Eintrag ausschließen' : 'Eintrag einschließen'}
                      />
                    </div>
                    <div className={styles.ticketContent}>
                      <div className={styles.ticketInfo}>
                        <div className={styles.ticketHeader}>
                          <h4>{item.ticket.title}</h4>
                          <span className={styles.duration}>{formatDuration(item.duration)}</span>
                        </div>
                        <div className={styles.tagRow}>
                          {bucket && (
                            <div className={styles.bucketTag}>
                              {bucket.title}
                            </div>
                          )}
                          <div
                            className={`${styles.projectTag} ${styles.clickable} ${!hasProject ? styles.noProject : ''}`}
                            onClick={() => handleOpenProjectModal(index)}
                            title="Projekt/Aufgabe ändern"
                          >
                            📁 {hasProject ? item.ticket.projectRef?.name || item.ticket.project : 'Kein Projekt'}
                            {hasTask && <span className={styles.taskName}> → {item.ticket.taskRef.name}</span>}
                            <i className="bi bi-pencil-fill" style={{ marginLeft: '6px', fontSize: '10px', opacity: 0.7 }}></i>
                          </div>
                        </div>
                      </div>
                      <div className={styles.inputContainer}>
                        <label>Rapport-Eintrag:</label>
                        <textarea
                          value={item.userInput}
                          onChange={(e) => handleInputChange(index, e.target.value)}
                          placeholder="AI-generierter Vorschlag wird hier angezeigt..."
                          className={styles.rapportInput}
                          rows={3}
                          disabled={!item.included}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button onClick={handleClose} className={styles.cancelButton}>
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            className={styles.submitButton}
            disabled={isSubmitting || rapportData.length === 0 || !zohoEmail || emailLoading || rapportData.filter(r => r.included).length === 0}
            title={!zohoEmail ? 'Zoho E-Mail in Einstellungen hinterlegen' : ''}
          >
            {isSubmitting ? 'Sende...' : emailLoading ? 'Prüfe...' : `An Zoho senden (${rapportData.filter(r => r.included).length})`}
          </button>
        </div>
      </div>

      {/* Project Selection Modal */}
      <ProjectSelectionModal
        isOpen={projectModalOpen}
        onClose={() => {
          setProjectModalOpen(false);
          setEditingTicketIndex(null);
        }}
        onSelectProject={handleProjectSelect}
        currentProject={editingTicketIndex !== null ? rapportData[editingTicketIndex]?.ticket?.projectRef : null}
        currentTask={editingTicketIndex !== null ? rapportData[editingTicketIndex]?.ticket?.taskRef : null}
      />
    </div>
  );
};

export default RapportModal;
